import { SlidersHorizontal, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { fieldValues, type FilterField, type FilterOption } from './filters';
import { MonthPicker } from './MonthPicker';
import { PageSizeSelect } from './PageSizeSelect';
import type { PageSize } from './use-list-params';
import { activeView, type ListView } from './ViewTabs';

/** Radix Select cannot hold an empty value, so «Tutti» is this stand-in. */
const ALL = '__all__';

/** The select trigger's own `min-h-[38px]` is set per size, so the phone's 44 px is set per size too. */
const TALL_TRIGGER = 'w-full data-[size=default]:min-h-11';

type Sort = {
  readonly label: string;
  readonly value: string;
  readonly options: readonly FilterOption[];
  readonly onChange: (value: string) => void;
};

type Views = {
  readonly label: string;
  readonly items: readonly ListView[];
  readonly current: { get: (key: string) => string | null };
  readonly onSelect: (view: ListView) => void;
  /** Entries that leave the list for another page (not a view); they sit last in the select. */
  readonly links?: readonly ViewLink[] | undefined;
};

export type ViewLink = {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  /** Where it leads: the wide screen shows it as a link beside the view tabs (#952 M4). */
  readonly to?: string;
};

const LINK_PREFIX = 'link:';

/** The phone's «Vista»: every view, and the links that sit with them, in one select (audit H1/H2). */
function ViewSelect({ views }: { readonly views: Views }) {
  const { t } = useTranslation('common');
  const active = activeView(views.items, views.current);
  const links = views.links ?? [];
  return (
    <Labelled label={t('list.view')}>
      <Select
        value={active?.id ?? ''}
        onValueChange={(next) => {
          if (next.startsWith(LINK_PREFIX)) {
            links.find((link) => `${LINK_PREFIX}${link.id}` === next)?.onSelect();
            return;
          }
          const view = views.items.find((item) => item.id === next);
          if (view) views.onSelect(view);
        }}
      >
        <SelectTrigger aria-label={t('list.view')} className={TALL_TRIGGER}>
          <SelectValue placeholder={t('list.viewCustom')} />
        </SelectTrigger>
        <SelectContent>
          {views.items.map((view) => (
            <SelectItem key={view.id} value={view.id}>
              {view.label}
            </SelectItem>
          ))}
          {links.map((link) => (
            <SelectItem key={link.id} value={`${LINK_PREFIX}${link.id}`}>
              {link.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Labelled>
  );
}

function Labelled({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-muted-foreground text-sm font-medium">{label}</span>
      {children}
    </div>
  );
}

function FieldSelect({
  field,
  value,
  onFilter,
}: {
  readonly field: FilterField;
  readonly value: string | null;
  readonly onFilter: (key: string, value: string | null) => void;
}) {
  const { t } = useTranslation('common');
  return (
    <Labelled label={field.label}>
      <Select
        value={value ?? ALL}
        onValueChange={(next) => onFilter(field.key, next === ALL ? null : next)}
      >
        <SelectTrigger aria-label={field.label} className={TALL_TRIGGER}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('list.all')}</SelectItem>
          {field.options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Labelled>
  );
}

/**
 * A `multiple` field on the phone: a checkbox for each option, ticked while its value is in force,
 * the indented ones under the option before them. A long list scrolls inside the sheet.
 */
function FieldChecks({
  field,
  value,
  onFilter,
}: {
  readonly field: FilterField;
  readonly value: string | null;
  readonly onFilter: (key: string, value: string | null) => void;
}) {
  const chosen = fieldValues(field, value);
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-muted-foreground mb-1.5 text-sm font-medium">{field.label}</legend>
      <div className="border-border flex max-h-64 flex-col overflow-y-auto overscroll-contain rounded-md border px-2 py-1">
        {field.options.map((option) => {
          const checked = chosen.includes(option.value);
          const next = checked
            ? chosen.filter((one) => one !== option.value)
            : [...chosen, option.value];
          return (
            <label
              key={option.value}
              className={`flex min-h-11 cursor-pointer items-center gap-3 text-sm${option.indent ? ' pl-7' : ''}`}
              data-indent={option.indent ? '' : undefined}
            >
              <Checkbox
                value={option.value}
                checked={checked}
                onChange={() => onFilter(field.key, next.length === 0 ? null : next.join(','))}
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * The phone's «Filtri e ordine» (#956, audit H1 and H3): the views, the order and every filter in
 * one bottom sheet, above the tab bar, instead of rows of controls that push the records off the
 * first screen. A dialog: Radix traps the focus, Escape closes, the focus returns to the button.
 * Each choice applies at once (it is written to the address, as on the wide screen), so «Fatto»
 * only closes.
 */
export function FiltersSheet({
  label,
  count,
  sort,
  views,
  viewsAside,
  fields,
  active,
  onFilter,
  onClear,
  narrowed,
  pageSize,
}: {
  readonly label: string;
  /** How many filters narrow the list, for the button. */
  readonly count: number;
  readonly sort?: Sort | undefined;
  readonly views?: Views | undefined;
  readonly viewsAside?: ReactNode;
  readonly fields: readonly FilterField[];
  readonly active: Readonly<Record<string, string | null>>;
  readonly onFilter: (key: string, value: string | null) => void;
  readonly onClear: () => void;
  readonly narrowed: boolean;
  readonly pageSize?:
    { readonly value: PageSize; readonly onChange: (size: PageSize) => void } | undefined;
}) {
  const { t } = useTranslation('common');
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="min-h-11 min-w-0 flex-1 justify-start"
          aria-label={count > 0 ? t('list.filtersAndSortActive', { count }) : undefined}
        >
          <SlidersHorizontal aria-hidden="true" />
          {t('list.filtersAndSort')}
          {count > 0 ? (
            <span
              aria-hidden="true"
              className="bg-primary text-primary-foreground ml-auto rounded-full px-2 text-xs"
            >
              {count}
            </span>
          ) : null}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        aria-label={label}
        className="max-h-[85dvh] overflow-y-auto rounded-t-xl p-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]"
      >
        <div className="flex items-center justify-between gap-2">
          <SheetTitle>{t('list.filtersAndSort')}</SheetTitle>
          <SheetClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-mr-2"
              aria-label={t('list.close')}
            >
              <X aria-hidden="true" />
            </Button>
          </SheetClose>
        </div>
        <SheetDescription className="sr-only">{label}</SheetDescription>
        {views ? (
          <div className="flex min-w-0 flex-col gap-2">
            <ViewSelect views={views} />
            {viewsAside}
          </div>
        ) : null}
        {sort ? (
          <Labelled label={sort.label}>
            <Select value={sort.value} onValueChange={sort.onChange}>
              <SelectTrigger aria-label={sort.label} className={TALL_TRIGGER}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sort.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Labelled>
        ) : null}
        {fields.map((field) =>
          field.monthPicker ? (
            <MonthPicker
              key={field.key}
              label={field.label}
              value={active[field.key] ?? null}
              onChange={(value) => onFilter(field.key, value)}
            />
          ) : field.multiple ? (
            <FieldChecks
              key={field.key}
              field={field}
              value={active[field.key] ?? null}
              onFilter={onFilter}
            />
          ) : (
            <FieldSelect
              key={field.key}
              field={field}
              value={active[field.key] ?? null}
              onFilter={onFilter}
            />
          ),
        )}
        {pageSize ? <PageSizeSelect value={pageSize.value} onChange={pageSize.onChange} /> : null}
        <div className="flex items-center justify-between gap-2">
          {narrowed ? (
            <Button type="button" variant="ghost" className="min-h-11" onClick={onClear}>
              {t('list.clear')}
            </Button>
          ) : (
            <span />
          )}
          <SheetClose asChild>
            <Button type="button" className="min-h-11">
              {t('list.done')}
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
